import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { Alert, Label, Select } from '@/shared/components';
import type { CreateTemplateFormValues } from '../schemas/documentTemplate.schema';
import { DealerOwnerSelect } from './DealerOwnerSelect';

const KIND_OPTIONS = [
  { value: 'platform', label: 'Platform' },
  { value: 'dealer', label: 'A dealer' },
];

interface Props {
  control: Control<CreateTemplateFormValues>;
  setValue: UseFormSetValue<CreateTemplateFormValues>;
  isInternal: boolean;
  hasDealers: boolean;
}

export function TemplateOwnerField({ control, setValue, isInternal, hasDealers }: Props) {
  const ownerKind = useWatch({ control, name: 'ownerKind' });

  if (!isInternal && !hasDealers) {
    return (
      <Alert variant="warning">
        <span>You have no dealer to own a template, so you cannot create one.</span>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {isInternal && (
        <div>
          <Label htmlFor="template-owner-kind">Owner</Label>
          <Controller
            name="ownerKind"
            control={control}
            render={({ field }) => (
              <Select
                id="template-owner-kind"
                options={KIND_OPTIONS}
                value={field.value}
                ariaLabel="Template owner"
                onChange={(value) => {
                  field.onChange(value);
                  if (value === 'platform') setValue('ownerDealerId', null);
                }}
              />
            )}
          />
        </div>
      )}
      {ownerKind === 'dealer' && (
        <div>
          <Label htmlFor="template-owner-dealer">Dealer</Label>
          <Controller
            name="ownerDealerId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <DealerOwnerSelect id="template-owner-dealer" value={field.value} onChange={field.onChange} />
                {error && (
                  <p role="alert" className="mt-1.5 text-xs text-mp-error">
                    {error.message}
                  </p>
                )}
              </>
            )}
          />
        </div>
      )}
    </div>
  );
}
